import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { HostedAgentRoutineFormState } from "../utils/hosted-agent-routines"

import { getAgentDeliveryOptions } from "@/api/client/hosted-agent-deliveries"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { SettingsService } from "@/services/settings-service"

export function HostedAgentRoutineDeliveryEditor({
  agentId,
  routine,
  onChange,
}: {
  agentId: string | undefined
  routine: HostedAgentRoutineFormState
  onChange: (changes: Partial<HostedAgentRoutineFormState>) => void
}) {
  const { t } = useTranslation()
  const options = useQuery({
    queryKey: ["agent-delivery-options", agentId],
    queryFn: () => getAgentDeliveryOptions(agentId!),
    enabled: !!agentId,
    retry: false,
  })
  const owner = !routine.createdBy || routine.createdBy === SettingsService.getInstance().claims.oid
  const update = (change: Partial<typeof routine.delivery>) =>
    onChange({ delivery: { ...routine.delivery, ...change } })
  const split = (value: string) =>
    value
      .split(/[,;\n]/)
      .map((item) => item.trim())
      .filter(Boolean)
  return (
    <fieldset className="space-y-4 rounded-lg border p-4" disabled={!owner}>
      <legend className="px-1 text-sm font-semibold">
        {t("agents.routineDelivery.title", { defaultValue: "Connection and delivery" })}
      </legend>
      {!owner ? (
        <p className="text-muted-foreground text-sm">
          {t("agents.routineDelivery.owner_only", {
            defaultValue:
              "Only the routine owner can change its connection and delivery permissions.",
          })}
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`routine-m365-${routine.id}`}>
          {t("agents.routineDelivery.m365", { defaultValue: "Use my Microsoft 365 connection" })}
        </Label>
        <Switch
          checked={routine.useOwnerM365Connection}
          id={`routine-m365-${routine.id}`}
          onCheckedChange={(useOwnerM365Connection) => onChange({ useOwnerM365Connection })}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        {t("agents.routineDelivery.required", {
          defaultValue:
            "When enabled, a missing or expired connection stops the run. An incomplete report must not be distributed as complete.",
        })}
      </p>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`routine-results-${routine.id}`}>
          {t("agents.routineDelivery.save_results", {
            defaultValue: "Save routine results and enable configured delivery",
          })}
        </Label>
        <Switch
          checked={routine.hasDeliveryPolicy}
          id={`routine-results-${routine.id}`}
          onCheckedChange={(hasDeliveryPolicy) => onChange({ hasDeliveryPolicy })}
        />
      </div>
      {!routine.hasDeliveryPolicy ? (
        <p className="text-muted-foreground text-xs">
          {t("agents.routineDelivery.results_off", {
            defaultValue:
              "This routine does not publish reports or send configured deliveries. Its run history is retained.",
          })}
        </p>
      ) : null}
      {!options.data?.available_channels.length ? (
        <p className="text-muted-foreground text-sm">
          {t("agents.routineDelivery.setup_required", {
            defaultValue:
              "Save the agent and ask an administrator to configure delivery channels. Existing settings are preserved.",
          })}
        </p>
      ) : null}
      {(["email", "teams"] as const).map((channel) => (
        <div key={channel} className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor={`routine-${channel}-${routine.id}`}>
              {t(`agents.routineDelivery.${channel}`, {
                defaultValue: channel === "email" ? "Email" : "Teams",
              })}
            </Label>
            <Switch
              checked={routine.delivery.channels.includes(channel)}
              id={`routine-${channel}-${routine.id}`}
              disabled={
                !routine.hasDeliveryPolicy ||
                (!routine.delivery.channels.includes(channel) &&
                  !options.data?.available_channels.includes(channel))
              }
              onCheckedChange={(checked) =>
                update({
                  channels: checked
                    ? [...routine.delivery.channels, channel]
                    : routine.delivery.channels.filter((item) => item !== channel),
                })
              }
            />
          </div>
          {routine.delivery.channels.includes(channel) ? (
            <>
              <Label htmlFor={`routine-${channel}-recipients-${routine.id}`}>
                {t(`agents.routineDelivery.${channel}_recipients`, {
                  defaultValue:
                    channel === "email"
                      ? "Email recipients (comma separated)"
                      : "Teams destinations",
                })}
              </Label>
              {channel === "email" ? (
                <RecipientInput
                  disabled={!routine.hasDeliveryPolicy}
                  id={`routine-email-recipients-${routine.id}`}
                  initialValue={routine.delivery.email_recipients.join(", ")}
                  onChange={(value) => update({ email_recipients: split(value) })}
                />
              ) : (
                <div className="space-y-2">
                  {options.data?.teams_destinations.map((destination) => (
                    <label key={destination.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={routine.delivery.teams_destination_ids.includes(destination.id)}
                        disabled={!routine.hasDeliveryPolicy}
                        onCheckedChange={(checked) =>
                          update({
                            teams_destination_ids:
                              checked === true
                                ? [...routine.delivery.teams_destination_ids, destination.id]
                                : routine.delivery.teams_destination_ids.filter(
                                    (id) => id !== destination.id
                                  ),
                          })
                        }
                      />
                      {destination.name || destination.id}
                    </label>
                  ))}
                  {options.data &&
                  routine.delivery.teams_destination_ids.some(
                    (id) =>
                      !options.data.teams_destinations.some((destination) => destination.id === id)
                  ) ? (
                    <p className="text-sm" role="alert">
                      {t("agents.routineDelivery.destination_missing", {
                        defaultValue:
                          "A saved Teams destination is no longer configured. Ask an administrator to restore it or disable Teams delivery.",
                      })}
                    </p>
                  ) : null}
                </div>
              )}
            </>
          ) : null}
        </div>
      ))}
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`routine-content-${routine.id}`}>
          {t("agents.routineDelivery.include_content", {
            defaultValue: "Include report content in deliveries",
          })}
        </Label>
        <Switch
          checked={routine.delivery.include_content}
          disabled={!routine.hasDeliveryPolicy}
          id={`routine-content-${routine.id}`}
          onCheckedChange={(include_content) => update({ include_content })}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`routine-audience-${routine.id}`}>
          {t("agents.routineDelivery.publish", {
            defaultValue: "Share saved results with everyone who can access this agent",
          })}
        </Label>
        <Switch
          checked={routine.delivery.audience === "agent_audience"}
          disabled={!routine.hasDeliveryPolicy}
          id={`routine-audience-${routine.id}`}
          onCheckedChange={(checked) =>
            update({ audience: checked ? "agent_audience" : "private" })
          }
        />
      </div>
      <p className="text-muted-foreground text-xs">
        {t("agents.routineDelivery.private", {
          defaultValue:
            "Saved results stay private by default. External recipients are validated against the deployment's delivery policy.",
        })}
      </p>
    </fieldset>
  )
}

function RecipientInput({
  disabled,
  id,
  initialValue,
  onChange,
}: {
  disabled: boolean
  id: string
  initialValue: string
  onChange: (value: string) => void
}) {
  const [value, setValue] = useState(initialValue)
  return (
    <Input
      disabled={disabled}
      id={id}
      value={value}
      onChange={(event) => {
        setValue(event.target.value)
        onChange(event.target.value)
      }}
    />
  )
}
